/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import React, { useCallback, useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";
import { Instance } from "tippy.js";
import AlignJustifyIcon from "../../../assets/icon/CampaignNotesToolbar/align-justify-icon";
import AlignLeftIcon from "../../../assets/icon/CampaignNotesToolbar/align-left-icon";
import AlignMiddleIcon from "../../../assets/icon/CampaignNotesToolbar/align-middle-icon";
import AlignRightIcon from "../../../assets/icon/CampaignNotesToolbar/align-right-icon";
import ChecklistIcon from "../../../assets/icon/CampaignNotesToolbar/checklist-icon";
import FormatTextIcon from "../../../assets/icon/CampaignNotesToolbar/format-text-icon";
import HorizontalSeparatorIcon from "../../../assets/icon/CampaignNotesToolbar/horizontal-separator-icon";
import IndentIcon from "../../../assets/icon/CampaignNotesToolbar/indent-icon";
import LinkAttachIcon from "../../../assets/icon/CampaignNotesToolbar/link-attach-icon";
import OutdentIcon from "../../../assets/icon/CampaignNotesToolbar/outdent-icon";
import { EmailMode } from "../../../atoms/EmailAtom";
import useClickOutside from "../../../hook/useClickOutside";
import useLinkPopover from "../../../hook/useLinkPopover";
import { notSelect } from "../../../styles/general";
import {
  getSelectedAnchorElement,
  getSelectedEditorText,
  getSelectedRange,
} from "../../../utils/LinkUtils";
import MergeFields from "../../NewEmail/MergeFields";
import useNoteLinkPopover from "./NoteEditor/useNoteLinkPopover";
import NotePopoverFormatting from "./NotePopoverFormatting";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% + 50px);
  height: 45px;
  background-color: #f9f9f9;
  margin: 18px 0px;
  border-top: 1px solid rgba(112, 112, 112, 0.15);
`;

const ToolbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 372px;
  height: 45px;
`;

const ToolbarItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  cursor: pointer;
  height: 100%;
  width: 34px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Divider = styled.div`
  height: 20px;
  width: 1px;
  background-color: #dddddd;
`;

const TooltipStyles = css`
  width: fit-content !important;
  padding: 2px 4px !important;
  margin-top: 5px !important;
  ${notSelect}
`;

enum AlignType {
  LEFT = "Left",
  MIDDLE = "Middle",
  RIGHT = "Right",
  JUSTIFY = "Justify",
}

const IconWrapper = styled.div`
  :hover {
    > svg > rect {
      fill: #eaeaea;
    }
  }

  > svg > rect {
    fill: #f9f9f9;
  }
`;

interface NoteModalToolbarProps {
  editor: Editor;
  tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;
  disableTaskList?: boolean;
  mode?: EmailMode;
  index?: number;
  isNoteEditor?: boolean;
}

export default function EditorToolbar({
  editor,
  tooltips,
  disableTaskList,
  mode,
  index,
  isNoteEditor,
}: NoteModalToolbarProps) {
  const { openLinkModal } = isNoteEditor
    ? useNoteLinkPopover(editor, tooltips)
    : useLinkPopover(editor, tooltips, mode, index);
  const [openDropView, setOpenDropView] = useState(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));

  const textAlign = editor?.isActive({ textAlign: "left" })
    ? "left"
    : editor?.isActive({ textAlign: "right" })
    ? "right"
    : editor?.isActive({ textAlign: "center" })
    ? "center"
    : "justify";

  const getAlignItem = useMemo(() => {
    switch (textAlign) {
      case "left":
        return (
          <IconWrapper>
            <AlignLeftIcon />
          </IconWrapper>
        );
      case "center":
        return (
          <IconWrapper>
            <AlignMiddleIcon />
          </IconWrapper>
        );
      case "right":
        return (
          <IconWrapper>
            <AlignRightIcon />
          </IconWrapper>
        );
      case "justify":
        return (
          <IconWrapper>
            <AlignJustifyIcon />
          </IconWrapper>
        );
      default:
        return (
          <IconWrapper>
            <AlignLeftIcon />
          </IconWrapper>
        );
    }
  }, [textAlign]);

  const handleAlignTypes = useCallback(() => {
    switch (textAlign) {
      case "left":
        editor.chain().setTextAlign("center").run();
        break;

      case "center":
        editor.chain().setTextAlign("right").run();
        break;

      case "right":
        editor.chain().setTextAlign("justify").run();
        break;

      default:
        editor.chain().setTextAlign("left").run();
        break;
    }
  }, [textAlign]);

  function handleLink() {
    const selectedAnchor = getSelectedAnchorElement();
    const selectedText = getSelectedEditorText(editor);
    const range = getSelectedRange();

    if (!range) return;
    if (!selectedAnchor && !selectedText) return;

    const linkURL = selectedAnchor?.getAttribute("data-id");
    const linkLabel = selectedAnchor?.getAttribute("data-label");
    const tooltipId = selectedAnchor?.getAttribute("data-tooltip-id");

    openLinkModal(
      !!selectedAnchor,
      selectedText,
      range,
      tooltipId,
      linkURL,
      linkLabel
    );
  }

  return (
    <Container
      onMouseDown={(e) => {
        setOpenDropView(false);
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ToolbarContent>
        <ToolbarItem
          onMouseDown={(e) => {
            setOpenDropView(true);
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{ position: "relative" }}
          data-tip
          key={"chooseTextStyle"}
          data-for="chooseTextStyle"
        >
          <IconWrapper>
            <FormatTextIcon />
          </IconWrapper>
          {openDropView && (
            <div ref={wrapperRef}>
              <NotePopoverFormatting editor={editor} />
            </div>
          )}
          {!openDropView && (
            <ReactTooltip
              id="chooseTextStyle"
              place="bottom"
              effect="solid"
              backgroundColor="#333333"
              arrowColor="transparent"
              css={css`
                ${TooltipStyles} margin-left: 44px !important;
              `}
            >
              <TooltipText>Choose text style</TooltipText>
            </ReactTooltip>
          )}
        </ToolbarItem>

        {disableTaskList && (
          <ToolbarItem
            onMouseDown={(e) => {
              if (!editor.isFocused) return;
              editor.commands.toggleTaskList();
              e.preventDefault();
              e.stopPropagation();
            }}
            data-tip
            key={"makeChecklist"}
            data-for="makeChecklist"
          >
            <IconWrapper>
              <ChecklistIcon height={45} />
            </IconWrapper>
            <ReactTooltip
              id="makeChecklist"
              place="bottom"
              effect="solid"
              backgroundColor="#333333"
              arrowColor="transparent"
              css={css`
                ${TooltipStyles} margin-left: 43px !important;
              `}
            >
              <TooltipText>Make a checklist</TooltipText>
            </ReactTooltip>
          </ToolbarItem>
        )}

        <ToolbarItem
          onMouseDown={(e) => {
            if (!editor.isFocused) return;
            handleAlignTypes();
            e.preventDefault();
            e.stopPropagation();
          }}
          data-tip
          key={"align"}
          data-for="align"
        >
          {getAlignItem}
          <ReactTooltip
            id="align"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 12px !important;
            `}
          >
            <TooltipText>Align</TooltipText>
          </ReactTooltip>
        </ToolbarItem>

        <ToolbarItem
          onMouseDown={(e) => {
            if (!editor.isFocused) return;
            handleLink();
            e.preventDefault();
            e.stopPropagation();
          }}
          data-tip
          key={"addLink"}
          data-for="addLink"
        >
          <IconWrapper>
            <LinkAttachIcon />
          </IconWrapper>
          <ReactTooltip
            id="addLink"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 27px !important;
            `}
          >
            <TooltipText>Add a link</TooltipText>
          </ReactTooltip>
        </ToolbarItem>

        <Divider />

        {/* TODO: This Quote button is already correct layout-wise, but it
            still need to create his functionality in tiptap
          */}
        {/* <ToolbarItem
          onClick={() => {}}
          data-tip
          key={"quote"}
          data-for="quote"
          onMouseEnter={() =>
            setToolbarOptionsHover((prevState) => ({
              ...prevState,
              isQuote: true,
            }))
          }
          onMouseLeave={() =>
            setToolbarOptionsHover((prevState) => ({
              ...prevState,
              isQuote: false,
            }))
          }
        >
           <IconWrapper isHovered={toolbarOptionsHover.isQuote}>
            <QuoteIcon />
          </IconWrapper>
          <ReactTooltip
            id="quote"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 15px !important;
            `}
          >
            <TooltipText>Quote</TooltipText>
          </ReactTooltip>
        </ToolbarItem> */}

        <ToolbarItem
          onMouseDown={(e) => {
            if (!editor.isFocused) return;
            editor.chain().setBlockquote().run();
            e.preventDefault();
            e.stopPropagation();
          }}
          data-tip
          key={"indent"}
          data-for="indent"
        >
          <IconWrapper>
            <IndentIcon />
          </IconWrapper>
          <ReactTooltip
            id="indent"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 18px !important;
            `}
          >
            <TooltipText>Indent</TooltipText>
          </ReactTooltip>
        </ToolbarItem>

        <ToolbarItem
          onMouseDown={(e) => {
            if (!editor.isFocused) return;
            editor.chain().unsetBlockquote().run();
            e.preventDefault();
            e.stopPropagation();
          }}
          data-tip
          key={"outdent"}
          data-for="outdent"
        >
          <IconWrapper>
            <OutdentIcon />
          </IconWrapper>
          <ReactTooltip
            id="outdent"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 22px !important;
            `}
          >
            <TooltipText>Outdent</TooltipText>
          </ReactTooltip>
        </ToolbarItem>

        <ToolbarItem
          onMouseDown={(e) => {
            if (!editor.isFocused) return;
            editor.chain().setHorizontalRule().run();
            e.preventDefault();
            e.stopPropagation();
          }}
          data-tip
          key={"insertHorizontalSeparator"}
          data-for="insertHorizontalSeparator"
        >
          <IconWrapper>
            <HorizontalSeparatorIcon />
          </IconWrapper>
          <ReactTooltip
            id="insertHorizontalSeparator"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 76px !important;
            `}
          >
            <TooltipText>Insert horizontal separator</TooltipText>
          </ReactTooltip>
        </ToolbarItem>

        {mode === "campaign" && (
          <ToolbarItem data-tip key={"mergeFilds"} data-for="mergeFilds">
            <MergeFields editor={editor} isCampaign>
              <div></div>
            </MergeFields>
          </ToolbarItem>
        )}
      </ToolbarContent>
    </Container>
  );
}
